import * as yup from "yup";
import { REGEX, REQUEST_TYPES, ROLES } from "shared/config";
import { useRequestPersistedStore } from "entities/Request";
import i18n from "shared/i18n";

export const getBasicDetailsValidationSchema = (
  minimumAdvanceAmount,
  requestType
) =>
  yup.object().shape({
    supervisor_email: yup
      .string()
      .required(
        i18n.t("validations.email.is_required", {
          ns: "RequestBasicDetails",
        })
      )
      .email(
        i18n.t("validations.email.invalid_email", {
          ns: "RequestBasicDetails",
        })
      )
      .matches(
        REGEX.is_valid_email,
        i18n.t("validations.email.domain_specific_email", {
          ns: "RequestBasicDetails",
        })
      )
      .test(
        "supervisor-is-not-requestor",
        i18n.t("validations.email.supervisor-is-not-requestor", {
          ns: "RequestBasicDetails",
        }),
        (email, ctx) =>
          !ctx.parent.representation_id?.search_string
            ?.toLowerCase()
            .includes(email?.toLowerCase())
      )
      .test(
        "is-additional_acceptor-email",
        i18n.t(
          "validations.email.supervisor_email_same_as_additional_acceptor",
          {
            ns: "RequestBasicDetails",
          }
        ),
        (email, ctx) =>
          email?.toLowerCase() !==
          ctx.parent.additional_acceptor_email?.toLowerCase()
      ),
    additional_acceptor_email: yup
      .string()
      .nullable()
      .email(
        i18n.t("validations.email.invalid_email", {
          ns: "RequestBasicDetails",
        })
      )
      .matches(
        REGEX.is_valid_email,
        i18n.t("validations.email.domain_specific_email", {
          ns: "RequestBasicDetails",
        })
      )
      .test(
        "additional-acceptor-is-not-requestor",
        i18n.t("validations.email.additional-acceptor-is-not-requestor", {
          ns: "RequestBasicDetails",
        }),
        (email, ctx) =>
          !ctx.parent.representation_id?.search_string
            ?.toLowerCase()
            .includes(email?.toLowerCase())
      )
      .test(
        "is-supervisor-email",
        i18n.t(
          "validations.email.additional_acceptor_same_as_supervisor_email",
          {
            ns: "RequestBasicDetails",
          }
        ),
        (email, ctx) =>
          email?.toLowerCase() !== ctx.parent.supervisor_email?.toLowerCase()
      ),
    advance_payment: yup
      .number()
      .test(
        "is-more-than",
        i18n.t("validations.advance_payment.is-more-than", {
          minimumAdvanceAmount: minimumAdvanceAmount ?? 0,
          ns: "RequestBasicDetails",
        }),
        (value) => value == null || value >= (minimumAdvanceAmount ?? 0)
      )
      .nullable(),
    cost_centre: yup.string().when((_, schema) =>
      requestType === REQUEST_TYPES.AMEX_BTA ||
      requestType === REQUEST_TYPES.CREDIT_CARD
        ? schema.notRequired().nullable()
        : schema
            .max(
              10,
              i18n.t("validations.cost_centre.max_length", {
                ns: "RequestBasicDetails",
              })
            )
            .required(
              i18n.t("validations.cost_centre.is_required", {
                ns: "RequestBasicDetails",
              })
            )
    ),
    comments: yup
      .string()
      .required(
        i18n.t("validations.comments.is_required", {
          ns: "RequestBasicDetails",
        })
      )
      .max(
        250,
        i18n.t("validations.comments.max_length", {
          ns: "RequestBasicDetails",
        })
      ),
    person_responsible: yup
      .number()
      .transform((_, originalValue) => originalValue?.value ?? null)
      .when((_, schema) =>
        useRequestPersistedStore.getState().viewRequestAs.role ===
        ROLES.ADMINISTRATION
          ? schema.nullable(false)
          : schema.nullable(true)
      )
      .typeError(
        i18n.t("validations.person_responsible.is_required", {
          ns: "RequestBasicDetails",
        })
      ),
    status: yup
      .string()
      .transform((_, originalValue) => originalValue?.value ?? null)
      .when((_, schema) =>
        useRequestPersistedStore.getState().viewRequestAs.role ===
        ROLES.ADMINISTRATION
          ? schema.nullable(false)
          : schema.nullable(true)
      )
      .typeError(
        i18n.t("validations.status.is_required", {
          ns: "RequestBasicDetails",
        })
      ),
  });
